import { NavigateFunction } from 'react-router';
import { clearAppState } from './clearAppState';
import { fetchWithCsrf } from '../api/fetch';
import { setMemberSession } from '../api/session';

interface LogoutOptions {
  navigate: NavigateFunction;
  setUser: (user: any) => void;
  resetConversations: () => void;
  resetArchive: () => void;
  notifications: any[];
  removeNotification: (index: number) => void;
  reactConfig: string | number;
}

/**
 * Performs a complete logout of the user including:
 * - API logout request
 * - Clearing all app state
 * - Resetting contexts
 * - Clearing notifications
 * - Navigation to home
 */
export const performLogout = async (options: LogoutOptions): Promise<void> => {
  const {
    navigate,
    setUser,
    resetConversations,
    resetArchive,
    notifications,
    removeNotification,
    reactConfig: reactConfigValue
  } = options;

  // A deliberate logout: its redirect to home is no lost session (api/session.ts)
  setMemberSession(false);
  try {
    const response = await fetchWithCsrf('/user/credentials/logout', { method: 'POST' });
    if (!response.ok) {
      // Logout did not happen server-side (e.g. stale CSRF token). Do a full
      // load so the app resyncs with the real session state instead of showing
      // a guest UI while the server session is still alive.
      window.location.assign('/');
      return;
    }
  } catch (e) {
    window.location.assign('/');
    return;
  }

  // Clear all app state
  clearAppState();

  // Broadcast a logout event so interested contexts can clear their in-memory state
  try {
    // Use a CustomEvent to allow future payloads if needed
    window.dispatchEvent(new CustomEvent('app:logout'));
  } catch (_) {
    // no-op in non-browser environments
  }
  
  // Reset context states
  resetConversations();
  resetArchive();
  
  // Clear notifications
  notifications.forEach((_, index) => removeNotification(index));
  
  // Clear user from app context
  setUser(undefined);
  
  // Navigate to home
  navigate('/');
  
  if (+reactConfigValue === 1) {
    navigate(0);
  }
};